"use client";

import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { APP_VERSION } from '@/utils/version';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { UploadCloud, Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for unique file names

interface BugReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ open, onOpenChange }) => {
  const currentVersion = 'v' + APP_VERSION;
  const { toast } = useToast();

  const [os, setOS] = useState('');
  const [browser, setBrowser] = useState('');
  const [timeOccurred, setTimeOccurred] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setOS('');
      setBrowser('');
      setTimeOccurred('');
      setDescription('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!os || !browser || !timeOccurred || !description) {
      toast({
        title: 'Missing info',
        description: 'Please fill out all required fields.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let uploadedFileURL: string | undefined = undefined;
      let mediaType: string | undefined = undefined;

      if (file) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExtension}`;
        const filePath = `bug-reports/${fileName}`; // Path within the 'bug-media' bucket

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('bug-media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false, // Do not overwrite existing files
          });

        if (uploadError) {
          console.error('File upload error:', uploadError);
          throw new Error(`Failed to upload file: ${uploadError.message}`);
        }

        // Get public URL of the uploaded file
        const { data: publicUrlData } = supabase.storage
          .from('bug-media')
          .getPublicUrl(filePath);

        if (publicUrlData) {
          uploadedFileURL = publicUrlData.publicUrl;
          mediaType = 'file'; // Set to 'file' to match the database constraint
        } else {
          throw new Error('Failed to get public URL for uploaded file.');
        }
      }

      const { error: insertError } = await supabase.from('bug_reports').insert({
        os,
        browser,
        time_occurred: new Date(timeOccurred).toISOString(), // Ensure ISO string for datetime
        version: currentVersion,
        media_type: mediaType, // Now sends 'file' or undefined
        screenshot_video_url: uploadedFileURL, // Use the uploaded file URL
        description,
      });

      if (insertError) {
        console.error('Database insert error:', insertError);
        throw new Error(`Failed to insert bug report: ${insertError.message}`);
      }

      toast({
        title: 'Thank you!',
        description: 'Your bug report has been submitted.',
      });

      onOpenChange(false);
    } catch (err: any) {
      console.error('Bug report submission failed:', err);
      toast({
        title: 'Error',
        description: `Failed to submit bug report: ${err.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Report a Bug</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="os">Operating System</Label>
            <Input
              id="os"
              placeholder="e.g., Windows 11"
              value={os}
              onChange={(e) => setOS(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="browser">Browser & Version</Label>
            <Input
              id="browser"
              placeholder="e.g., Chrome 123"
              value={browser}
              onChange={(e) => setBrowser(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeOccurred">When did it happen?</Label>
            <Input
              type="datetime-local"
              id="timeOccurred"
              value={timeOccurred}
              onChange={(e) => setTimeOccurred(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">What went wrong?</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Describe what you were doing when the bug occurred..."
            />
          </div>

          <div className="space-y-2">
            <Label>Attach Screenshot or Video (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="image/*,video/*"
                ref={fileInputRef}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
            {file && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <UploadCloud className="h-4 w-4" />
                {file.name}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
            Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportModal;